import { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { Send, Sparkles, User } from "lucide-react";
import { Input } from "../../components/ui/input";

const SUGGESTIONS = [
  "What were my best-selling products this month?",
  "Which products should I restock?",
  "Show me products with high sales but low profit.",
  "What are the top 5 opportunities to grow revenue?",
  "Which customers are likely to buy again?",
];

// Very small markdown renderer for bold, bullets, headings
function md(text) {
  if (!text) return "";
  let html = text
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^### (.+)$/gm, "<h4 class='font-outfit font-bold text-lg mt-3 mb-1'>$1</h4>")
    .replace(/^## (.+)$/gm, "<h3 class='font-outfit font-bold text-xl mt-3 mb-1'>$1</h3>")
    .replace(/^# (.+)$/gm, "<h2 class='font-serif-display text-2xl mt-3 mb-1'>$1</h2>")
    .replace(/^\s*[-*] (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul class='list-disc pl-5 space-y-1 my-2'>${m}</ul>`)
    .replace(/\n\n/g, "<br/><br/>").replace(/\n/g, "<br/>");
  return html;
}

export default function AdminAssistant() {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/admin/assistant/history").then(r => {
      const hist = r.data.reverse().flatMap(c => ([
        {role:"user", text:c.question}, {role:"assistant", text:c.answer}
      ]));
      setMessages(hist);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const question = (text ?? q).trim();
    if (!question || loading) return;
    setMessages(m => [...m, {role:"user", text:question}]);
    setQ(""); setLoading(true);
    try {
      const r = await api.post("/admin/assistant/ask", { question });
      setMessages(m => [...m, {role:"assistant", text: r.data.answer}]);
    } catch (e) {
      setMessages(m => [...m, {role:"assistant", text: "Sorry — I ran into an error. Please try again."}]);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#D4AF37]"/>
          <h1 className="text-3xl font-outfit font-bold">AI Business Assistant</h1>
        </div>
        <div className="text-sm text-stone-500 mt-1">Powered by Claude — ask anything about your sales, products, and customers.</div>
      </div>

      <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 overflow-auto bg-white border border-stone-200 rounded-lg p-6 space-y-4">
        {messages.length === 0 && !loading && (
          <div className="text-center py-10">
            <Sparkles className="w-10 h-10 mx-auto text-[#D4AF37] mb-4"/>
            <div className="font-serif-display text-2xl mb-2">Ask about your business</div>
            <div className="text-sm text-stone-500 mb-6">Try one of these to get started:</div>
            <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
              {SUGGESTIONS.map((s,i)=>(
                <button key={i} onClick={()=>send(s)} data-testid={`suggestion-${i}`}
                        className="text-xs border border-stone-200 rounded-full px-3 py-1.5 bg-stone-50 hover:bg-stone-100">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m,i) => (
          <div key={i} className={`flex gap-3 ${m.role==="user"?"justify-end":""}`}>
            {m.role==="assistant" && <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-[#D4AF37]"/></div>}
            <div className={`${m.role==="user"?"bg-[#3B2F2F] text-white":"bg-stone-50 border border-stone-200 text-stone-800"} rounded-lg px-4 py-3 max-w-[75%]`}>
              {m.role==="assistant" ? <div className="text-sm prose prose-sm" dangerouslySetInnerHTML={{__html: md(m.text)}}/> : <div className="text-sm">{m.text}</div>}
            </div>
            {m.role==="user" && <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center shrink-0"><User className="w-4 h-4"/></div>}
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#D4AF37] animate-pulse"/></div>
            <div className="bg-stone-50 border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-500">Thinking…</div>
          </div>
        )}
      </div>

      <form onSubmit={e=>{e.preventDefault(); send();}} className="mt-4 flex gap-2">
        <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Ask about sales, products, customers…" data-testid="assistant-input" className="flex-1 bg-white"/>
        <button type="submit" disabled={loading||!q.trim()} data-testid="assistant-send" className="bg-[#3B2F2F] text-white rounded-md px-4 flex items-center gap-2 disabled:bg-stone-300"><Send className="w-4 h-4"/>Send</button>
      </form>
    </div>
  );
}
